import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Zap, Globe } from 'lucide-react';
import AuthForm from '../components/organisms/AuthForm';
import Card from '../components/atoms/Card';

const AuthPage: React.FC = () => {
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');

  const features = [
    {
      icon: Shield,
      title: 'Bank-Level Security',
      description: 'End-to-end encryption with military-grade security protocols',
    },
    {
      icon: Lock,
      title: 'Blockchain Notarization',
      description: 'Immutable proof of authenticity using blockchain technology',
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Instant uploads and lightning-fast document retrieval',
    },
    {
      icon: Globe,
      title: 'Access Anywhere',
      description: 'Secure access from any device, anywhere in the world',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex">
      {/* Left Side - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-lg"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center shadow-xl">
              <Shield className="h-10 w-10 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Aegis Vault</h1>
              <p className="text-lg text-gray-600">Secure Document Management</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                The Future of Document Security
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Protect your most important documents with cutting-edge encryption, 
                blockchain verification, and seamless collaboration tools.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card glass className="p-4 border border-white/20">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-md"
        >
          <AuthForm mode={authMode} onModeChange={setAuthMode} />
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;